import classNames from "classnames";
import styles from "./styles/SpotlightTerrace.module.scss";
import React, {
  KeyboardEvent,
  Suspense,
  useEffect,
  useId,
  useRef,
  useState,
} from "react";
import { observer } from "mobx-react";
import { getCdnUrl } from "../../../../utils/SchoolBlocksUtilities";
import ApiClient from "../../../../backends/ApiClient";
import { runInAction } from "mobx";
import NotificationManager from "../../../notifications/NotificationManager";
import SpotlightTerraceTileCurationContainer from "./SpotlightTerraceTileCurationContainer";
import { useStores } from "../../../../stores/useStores";
import { useT } from "../../../utilities/TranslatedText";
import Button from "../../../utilities/Button";
import CTAButton from "../../../utilities/CTAButton";
import Image from "next/image";
import { reportError } from "../../../../utils/errorReporting";

const spotlightTileSize = 290;

const SpotlightTerraceTile = observer(
  ({
    item,
    terraceObj,
    idx,
    setActiveIndex,
    reorder,
    tileRef,
    onReorderKey,
    dataActive,
    dataPosition,
    isExpanded,
    style,
  }: {
    item: ISpotlightTile;
    terraceObj: ISpotlightTerrace;
    idx: number;
    setActiveIndex: (index: number) => void;
    reorder?: boolean;
    tileRef?: (element: HTMLElement | null) => void;
    onReorderKey?: (delta: number) => void;
    dataPosition?: string;
    dataActive?: boolean;
    isExpanded?: boolean;
    style?: React.CSSProperties;
  }) => {
    const { userStore, modalStore, gridStore } = useStores();

    const t = useT();
    const [showContent, setShowContent] = useState(false);
    const contentId = useId();
    const disclosureRef = useRef<HTMLButtonElement>(null);
    const closeRef = useRef<HTMLButtonElement>(null);
    useEffect(() => {
      if (showContent && document.activeElement === disclosureRef.current) {
        closeRef.current?.focus();
      }
    }, [showContent]);
    const hasContent = !!(item.message || item.buttonData.buttonToggle);

    const tileClassName = classNames(styles.tile, {
      [styles.tileExpanded]: isExpanded,
      [styles.contentVisible]: hasContent && !reorder && showContent,
      [styles.tileReorder]: reorder,
    });

    const handleDelete = async () => {
      try {
        let updatedTiles = [...(terraceObj.json_data?.spotlight?.tiles || [])];
        const client = await ApiClient();

        updatedTiles = updatedTiles.filter((tile) => tile.id !== item.id);

        const result = await client.PATCH(
          "/organizations/{organization_pk}/terraces/{id}/",
          {
            params: {
              path: {
                organization_pk: terraceObj.organization_id,
                id: terraceObj.id,
              },
            },
            body: {
              json_data: { spotlight: { tiles: updatedTiles } },
            } as never,
          },
        );

        runInAction(() => {
          gridStore.blocks.set(terraceObj.id, result);
        });
        NotificationManager.success("Tile successfully Deleted!");
        modalStore.removeTop();
      } catch (e) {
        reportError(e, { error_source: "handled" });
        NotificationManager.error("Something went wrong! Please try again.");
      }
    };
    const handleTileKeyDown = (e: KeyboardEvent) => {
      if (e.target !== e.currentTarget) return;
      if (reorder) {
        if (e.key === "ArrowLeft" || e.key === "ArrowUp") {
          e.preventDefault();
          onReorderKey?.(-1);
          return;
        }
        if (e.key === "ArrowRight" || e.key === "ArrowDown") {
          e.preventDefault();
          onReorderKey?.(1);
          return;
        }
        return;
      }
    };

    return (
      <div
        ref={tileRef}
        className={tileClassName}
        role={reorder ? "button" : undefined}
        tabIndex={reorder ? 0 : undefined}
        aria-label={reorder ? `Reorder ${item.title}` : undefined}
        onKeyDown={reorder ? handleTileKeyDown : undefined}
        onFocus={() => {
          if (!reorder) setActiveIndex(idx);
        }}
        onPointerEnter={(e) => {
          if (!reorder && e.pointerType === "mouse") setShowContent(true);
        }}
        onPointerLeave={(e) => {
          if (!e.currentTarget.contains(document.activeElement))
            setShowContent(false);
        }}
        data-active={dataActive}
        data-position={dataPosition}
        data-index={idx}
        style={style}
        onClick={(e) => {
          if (reorder || (e.target as HTMLElement).closest("button, a")) return;
          setActiveIndex(idx);
          setShowContent((prev) => !prev);
        }}
      >
        {userStore.isEditor && !reorder && (
          <Suspense fallback={null}>
            <SpotlightTerraceTileCurationContainer
              item={item}
              terraceObj={terraceObj}
              idx={idx}
              handleDelete={handleDelete}
            />
          </Suspense>
        )}

        <div className={styles.imageContainer}>
          <Image
            src={getCdnUrl(item.image)}
            alt={item.altText}
            width={spotlightTileSize}
            height={spotlightTileSize}
          />
        </div>
        <div className={styles.bannerLayer}>
          <div className={styles.tileTitle}>
            <h3>
              {reorder ? (
                item.title
              ) : (
                <Button
                  type="button"
                  className={styles.disclosureButton}
                  ref={disclosureRef}
                  tabIndex={hasContent && showContent ? -1 : undefined}
                  aria-expanded={hasContent ? showContent : undefined}
                  aria-controls={hasContent ? contentId : undefined}
                  onClick={() => {
                    setActiveIndex(idx);
                    if (hasContent) setShowContent((prev) => !prev);
                  }}
                >
                  {item.title}
                </Button>
              )}
            </h3>
          </div>
        </div>

        {hasContent && !reorder && (
          <div
            id={contentId}
            className={styles.content}
            inert={!showContent}
            aria-hidden={!showContent}
          >
            <Button
              ref={closeRef}
              type="button"
              className={styles.closeButton}
              aria-label={`${t("Close")}: ${item.title}`}
              onClick={() => {
                setShowContent(false);
                disclosureRef.current?.focus();
              }}
            >
              <span aria-hidden="true">×</span>
            </Button>
            {item.message && (
              <span className={styles.message}>{item.message}</span>
            )}
            {item.buttonData.buttonToggle &&
              item.buttonData.buttonText &&
              (item.buttonData.buttonLink ||
                item.buttonData.buttonLinkOrgId) && (
                <CTAButton
                  className={styles.tileButton}
                  buttonData={item.buttonData}
                >
                  <span>{item.buttonData.buttonText}</span>
                </CTAButton>
              )}
          </div>
        )}
      </div>
    );
  },
);

export default SpotlightTerraceTile;
